import { Vector2 } from 'three';
import {
  Areas,
  LogicalSpace,
  Obstacle,
  Point,
  Polygon,
  Rectangle,
  Segment,
} from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import {
  createEmptyAreas,
  offsetArea,
  offsetPolygon,
  offsetSegments,
  pointsToPolygon,
  pushAreaToItsCollection,
  rectangleToClosedPolygon,
} from '@/modules/floorplanDesign/helpers/helpers';
import { AtLeast1, minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { Area } from '@/modules/floorplanDesign/types';

import {
  getAreaRectangles,
  getCenter,
  getGroupAngleDelta,
  getPositionDelta,
  PoseTransform,
  rotateRectangleAround,
  transformArea,
  transformPolygon,
  transformSegments,
} from './helpers';
import { DimensionField, EditMode } from './types';

/**
 * @description One selected item, whatever its type: an area (a rectangle, or a highway's segments),
 * an obstacle (segments) or a rectangular logical space (a polygon). Lets a mixed selection be edited
 * like a selection of one type.
 */
export type DimensionItem =
  | { type: 'area'; area: Area }
  | { type: 'obstacle'; obstacle: Obstacle }
  | { type: 'logicalSpace'; logicalSpace: LogicalSpace };

type Center = ReturnType<typeof getCenter>;

/**
 * @description Wraps the areas, obstacles and logical spaces in one list of tagged DimensionItems,
 * so the dimension/arrange helpers can handle a mixed selection the same way.
 * toItemUpdates turns them back into separate lists.
 */
export const createItems = (
  areas: Area[],
  obstacles: Obstacle[],
  logicalSpaces: LogicalSpace[],
): DimensionItem[] => [
  ...areas.map((area): DimensionItem => ({ type: 'area', area })),
  ...obstacles.map((obstacle): DimensionItem => ({ type: 'obstacle', obstacle })),
  ...logicalSpaces.map((logicalSpace): DimensionItem => ({ type: 'logicalSpace', logicalSpace })),
];

export const getItemRectangles = (item: DimensionItem): Rectangle[] => {
  switch (item.type) {
    case 'area':
      return getAreaRectangles(item.area);
    case 'obstacle':
      return item.obstacle.segments.map(RectangleUtils.fromSegment);
    case 'logicalSpace': {
      const rectangle = RectangleUtils.polygonToRectangle(item.logicalSpace.polygon);
      return rectangle ? [rectangle] : [];
    }
    default:
      return assertNever(item);
  }
};

/** @description Paths (highways, obstacles) are made of segments, the rest of a rectangle */
const isPathItem = (item: DimensionItem): boolean =>
  item.type === 'obstacle' || (item.type === 'area' && isHighwayArea(item.area));

const rotatePointAround = <T extends Point>(point: T, center: Center, angle: number): T => {
  const rotated = new Vector2(point.x, point.y).rotateAround(
    new Vector2(center.centerX, center.centerY),
    angle,
  );
  return { ...point, x: rotated.x, y: rotated.y };
};

/** @description Turns a whole path as one piece; rotating the points keeps each side's width */
const rotateSegmentsAround = <T extends Segment>(
  segments: AtLeast1<T>,
  center: Center,
  angle: number,
): AtLeast1<T> =>
  minItemsArrayUtils.mapAtLeast1(segments, (segment) => ({
    ...segment,
    start: rotatePointAround(segment.start, center, angle),
    end: rotatePointAround(segment.end, center, angle),
  }));

/** @description Sets the length or width of the first segment (only single-segment paths get here) */
const resizeFirstSegment = <T extends Segment>(
  segments: AtLeast1<T>,
  field: 'length' | 'width',
  value: number,
): AtLeast1<T> =>
  minItemsArrayUtils.mapAtLeast1(segments, (segment, index) =>
    index === 0
      ? {
          ...segment,
          ...RectangleUtils.toPartialSegment({
            ...RectangleUtils.fromSegment(segment),
            [field]: value,
          }),
        }
      : segment,
  );

const mapPolygonPoints = (polygon: Polygon, map: (point: Point) => Point): Polygon =>
  pointsToPolygon(polygon.points.map(map)) ?? polygon;

export const offsetItem = (item: DimensionItem, offset: Vector2): DimensionItem => {
  switch (item.type) {
    case 'area':
      return { ...item, area: offsetArea(item.area, offset) };
    case 'obstacle':
      return {
        ...item,
        obstacle: { ...item.obstacle, segments: offsetSegments(item.obstacle.segments, offset) },
      };
    case 'logicalSpace': {
      const { logicalSpace } = item;
      const polygon = offsetPolygon(logicalSpace.polygon, offset) ?? logicalSpace.polygon;
      return { ...item, logicalSpace: { ...logicalSpace, polygon } };
    }
    default:
      return assertNever(item);
  }
};

export const rotateItemAround = (
  item: DimensionItem,
  center: Center,
  angle: number,
): DimensionItem => {
  switch (item.type) {
    case 'obstacle':
      return {
        ...item,
        obstacle: {
          ...item.obstacle,
          segments: rotateSegmentsAround(item.obstacle.segments, center, angle),
        },
      };
    case 'logicalSpace': {
      const { logicalSpace } = item;
      const polygon = mapPolygonPoints(logicalSpace.polygon, (point) =>
        rotatePointAround(point, center, angle),
      );
      return { ...item, logicalSpace: { ...logicalSpace, polygon } };
    }
    case 'area': {
      const { area } = item;
      if (isHighwayArea(area)) {
        return {
          ...item,
          area: { ...area, segments: rotateSegmentsAround(area.segments, center, angle) },
        };
      }
      return {
        ...item,
        area: { ...area, rectangle: rotateRectangleAround(area.rectangle, center, angle) },
      };
    }
    default:
      return assertNever(item);
  }
};

/** @description Rotates 90° or mirrors the item (see getPoseTransform) */
export const transformItem = (item: DimensionItem, poseTransform: PoseTransform): DimensionItem => {
  switch (item.type) {
    case 'area':
      return { ...item, area: transformArea(item.area, poseTransform) };
    case 'obstacle':
      return {
        ...item,
        obstacle: {
          ...item.obstacle,
          segments: transformSegments(item.obstacle.segments, poseTransform),
        },
      };
    case 'logicalSpace':
      return {
        ...item,
        logicalSpace: {
          ...item.logicalSpace,
          polygon: transformPolygon(item.logicalSpace.polygon, poseTransform),
        },
      };
    default:
      return assertNever(item);
  }
};

/** @description Turns the item around its own centre until its (first) rectangle has the angle */
const setItemAngle = (item: DimensionItem, angle: number): DimensionItem => {
  const rectangles = getItemRectangles(item);
  if (rectangles.length === 0) return item;
  return rotateItemAround(item, getCenter(rectangles), getGroupAngleDelta(angle, rectangles));
};

const setItemSize = (
  item: DimensionItem,
  field: 'length' | 'width',
  value: number,
): DimensionItem => {
  switch (item.type) {
    case 'obstacle':
      return {
        ...item,
        obstacle: {
          ...item.obstacle,
          segments: resizeFirstSegment(item.obstacle.segments, field, value),
        },
      };
    case 'logicalSpace': {
      const { logicalSpace } = item;
      const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
      if (!rectangle) return item;
      const polygon =
        rectangleToClosedPolygon({ ...rectangle, [field]: value }) ?? logicalSpace.polygon;
      return { ...item, logicalSpace: { ...logicalSpace, polygon } };
    }
    case 'area': {
      const { area } = item;
      if (isHighwayArea(area)) {
        return {
          ...item,
          area: { ...area, segments: resizeFirstSegment(area.segments, field, value) },
        };
      }
      return { ...item, area: { ...area, rectangle: { ...area.rectangle, [field]: value } } };
    }
    default:
      return assertNever(item);
  }
};

/**
 * @description Applies one dimension field to the items. Per item: every item gets the value.
 * As a group: X/Y move everything together, the angle turns everything around the selection centre
 * (a typed angle goes to the first item, the others keep their offsets), length and width are read-only.
 * Returns null when nothing changes.
 */
export const updateItemsField = (
  items: DimensionItem[],
  field: DimensionField,
  value: number,
  editMode: EditMode,
): DimensionItem[] | null => {
  const rectangles = items.flatMap(getItemRectangles);
  if (rectangles.length === 0) return null;
  const center = getCenter(rectangles);

  if (field === 'centerX' || field === 'centerY') {
    const toOffset = (delta: number) =>
      new Vector2(field === 'centerX' ? delta : 0, field === 'centerY' ? delta : 0);

    if (editMode === 'group') {
      const delta = getPositionDelta(field, value, center);
      return delta === 0 ? null : items.map((item) => offsetItem(item, toOffset(delta)));
    }
    return items.map((item) => {
      const itemRectangles = getItemRectangles(item);
      if (itemRectangles.length === 0) return item;
      return offsetItem(item, toOffset(getPositionDelta(field, value, getCenter(itemRectangles))));
    });
  }

  if (field === 'angle') {
    if (editMode === 'group') {
      const angleDelta = getGroupAngleDelta(value, rectangles);
      return angleDelta === 0
        ? null
        : items.map((item) => rotateItemAround(item, center, angleDelta));
    }
    return items.map((item) => setItemAngle(item, value));
  }

  if (editMode === 'group') return null;
  // A path can't have a zero length or width
  if (value <= 0 && items.some(isPathItem)) return null;
  return items.map((item) => setItemSize(item, field, value));
};

/**
 * @description Groups the items per kind into the shape fddmSvc.updateItems expects:
 * areas into their area-type collections, obstacles and logical spaces into their own lists
 */
export const toItemUpdates = (
  items: DimensionItem[],
): { areas: Areas; obstacles: Obstacle[]; logicalSpaces: LogicalSpace[] } => {
  const areas = createEmptyAreas();
  const obstacles: Obstacle[] = [];
  const logicalSpaces: LogicalSpace[] = [];

  items.forEach((item) => {
    if (item.type === 'area') pushAreaToItsCollection(areas, item.area);
    else if (item.type === 'obstacle') obstacles.push(item.obstacle);
    else logicalSpaces.push(item.logicalSpace);
  });

  return { areas, obstacles, logicalSpaces };
};
